import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { MutableRefObject, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { EMPLOYERS } from '@pflegia/ui/pflegia/constants/routes';
import { CardStatus, CardType } from '@pflegia/ui/pflegia/types/card';
import { InitiativeBadge } from '@pflegia/ui/shared/initiative-badge/InitiativeBadge';
import { shouldImageBeUnoptimized } from '@pflegia/ui/shared/utils/image-unoptimized';
import { removeCountryName } from '@pflegia/util/functions/job-position/helpers';

import EmojiText from '@components/emoji-text';
import { OffersDialogueButtonSkeleton } from '@components/skeleton/OffersDialogueButtonSkeleton';

import { useIsElementPinned } from '@hooks/use-is-element-pinned';

import jobPositionPlaceholder from '@public/assets/job-position-placeholder.jpg';

import { useRouterHelper } from '@shared/hooks/use-router-helper';

import defaultPerks from './DefaultJobPerks';

const OffersDialogueButton = dynamic(() => import('@components/dialogue'), { loading: () => <OffersDialogueButtonSkeleton />, ssr: false });

export interface JobDetailTitleProps {
  invitationType: CardType;
  jobPositionId: string;
  jobPositionTitle: string;
  status: CardStatus;
  titleRef?: MutableRefObject<HTMLDivElement | null>;
  openDialog: (connectionInfo: ConnectionInfo) => void;
  employerId: string;
  employerName: string;
  employerLogo?: string;
  address?: string;
  children?: React.ReactNode;
  isInitiative?: boolean;
  slug: string;
  isCoverImageShown?: boolean;
}

const JobDetailTitle = ({
  invitationType,
  jobPositionId,
  status,
  jobPositionTitle,
  titleRef,
  openDialog,
  employerId,
  employerName,
  employerLogo,
  address,
  children,
  isInitiative,
  slug,
  isCoverImageShown,
}: JobDetailTitleProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('jobDetails');
  const containerRef = useRef<HTMLDivElement>(null);
  const ref = titleRef || containerRef;
  const isPinned = useIsElementPinned(ref);
  const { isJobDetailsPage, isTransparentPath, isExternalJob } = useRouterHelper();
  const shouldShowInitiativeBadge = isInitiative && isJobDetailsPage;

  return (
    <div ref={ref}>
      <div
        className={twMerge(
          clsx(
            '-mx-6 flex flex-col gap-2 bg-white px-4 py-6 sm:mx-0 md:flex-row md:justify-between md:pt-6 lg:px-12',
            invitationType === 'invitation' && status === 'pending' && 'sm:rounded-b-2xl lg:rounded-b-none',
            invitationType === 'application' && (status === 'pending' || status === 'declined') && 'sm:rounded-b-2xl lg:rounded-b-none',
            invitationType === 'default' && 'sm:rounded-t-2xl sm:rounded-b-2xl lg:rounded-b-none',
          ),
        )}>
        {isTransparentPath ? (
          <div className='flex flex-1 gap-x-8'>
            <Image
              alt=''
              className='bg-img hidden h-24 w-24 rounded-full border-2 object-contain lg:block'
              height={96}
              src={employerLogo || jobPositionPlaceholder}
              unoptimized={employerLogo ? shouldImageBeUnoptimized(employerLogo) : undefined}
              width={96}
            />
            <div className='text-default flex flex-col'>
              {shouldShowInitiativeBadge && <InitiativeBadge className='mb-4' />}
              <Link
                target='_blank'
                rel='noopener noreferrer'
                href={`${EMPLOYERS}/${employerId}`}
                className='text-primary mb-2 text-base font-medium underline'>
                {employerName}
              </Link>
              <h1
                data-testid='job-detail-title'
                lang={language}
                className={clsx('flex-1 text-2xl font-semibold break-words hyphens-auto md:my-auto lg:block lg:text-4xl')}>
                {jobPositionTitle}
              </h1>
              {children && <div className='mt-4'>{children}</div>}
              {address && (
                <span className='mt-2 hidden lg:block'>
                  <EmojiText emoji={defaultPerks.address} className='-ml-2'>
                    {removeCountryName(address)}
                  </EmojiText>
                </span>
              )}
            </div>

            {invitationType === 'default' && (
              <div className='hidden flex-1 items-center justify-end lg:flex'>
                {(isJobDetailsPage || isCoverImageShown) && (
                  <OffersDialogueButton
                    className='my-auto h-max w-full whitespace-nowrap md:w-auto'
                    jobPositionId={jobPositionId}
                    label={t(isExternalJob ? 'contactPerson.apply' : 'overview.applyDesktop')}
                    status={status}
                    employerName={employerName}
                    jobTitle={jobPositionTitle}
                    openDialog={openDialog}
                    slug={slug}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className='flex flex-col gap-y-4'>
              {shouldShowInitiativeBadge && <InitiativeBadge />}
              <h1
                data-testid='job-detail-title'
                lang={language}
                className={clsx('text-default flex-1 text-xl font-semibold break-words hyphens-auto md:my-auto lg:block lg:text-3xl')}>
                {jobPositionTitle}
              </h1>
              {children}
            </div>
            <div className='hidden lg:flex'>
              {invitationType === 'default' && (
                <OffersDialogueButton
                  className='my-auto h-max w-full whitespace-nowrap md:w-auto'
                  jobPositionId={jobPositionId}
                  label={t('overview.applyDesktop')}
                  status={status}
                  employerName={employerName}
                  jobTitle={jobPositionTitle}
                  openDialog={openDialog}
                  slug={slug}
                />
              )}
              {invitationType === 'invitation' && status === 'pending' && (
                <OffersDialogueButton
                  className={clsx('my-auto hidden h-max whitespace-nowrap', isPinned && 'md:block')}
                  jobPositionId={jobPositionId}
                  employerName={employerName}
                  label={t('applicationHeader.accept')}
                  status='pending'
                  jobTitle={jobPositionTitle}
                  type='invitation'
                  openDialog={openDialog}
                  slug={slug}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JobDetailTitle;
